import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ticketAPI, clientAPI, applicationAPI, fileAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card } from '../components/ui/card';
import {
  ArrowLeft,
  AlertTriangle,
  Bug,
  Lightbulb,
  RefreshCw,
  Loader2,
  Send,
  X,
  Paperclip,
  FileText,
  Image,
  File
} from 'lucide-react';
import debounce from 'lodash/debounce';

const PRIORITIES = ['low', 'medium', 'high', 'critical'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const TicketForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [checkingSimilar, setCheckingSimilar] = useState(false);
  const [similarTickets, setSimilarTickets] = useState([]);
  const [clients, setClients] = useState([]);
  const [applications, setApplications] = useState([]);
  const [departments, setDepartments] = useState(['Finance', 'Sales', 'IT', 'HR', 'Management']);
  const [attachments, setAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    ticket_type: 'bug',
    client_id: '',
    application_id: '',
    department: user?.department || '',
    priority: 'medium'
  });

  const [errors, setErrors] = useState({});

  // Determine if user is an Intelvolks internal user (can pick any client)
  const isInternalUser = user?.organization === 'Intelvolks';

  useEffect(() => {
    fetchClients();
  }, []);

  // Auto-select client for external users once clients are loaded
  useEffect(() => {
    if (!isInternalUser && clients.length > 0 && !formData.client_id) {
      const matchedClient = clients.find(
        c => c.name.toLowerCase() === user?.organization?.toLowerCase()
      );
      if (matchedClient) {
        setFormData(prev => ({ ...prev, client_id: matchedClient.id }));
      }
    }
  }, [clients, isInternalUser, user?.organization]);

  useEffect(() => {
    if (formData.client_id) {
      fetchApplications(formData.client_id);
      // Update departments based on client
      const client = clients.find(c => c.id === formData.client_id);
      if (client?.departments?.length > 0) {
        setDepartments(client.departments);
      }
    } else {
      setApplications([]);
    }
  }, [formData.client_id, clients]);

  const fetchClients = async () => {
    try {
      const response = await clientAPI.getClients();
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const fetchApplications = async (clientId) => {
    try {
      const response = await applicationAPI.getApplications({ client_id: clientId });
      setApplications(response.data);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    }
  };

  // Debounced similar ticket check
  const checkSimilarTickets = useCallback(
    debounce(async (title, description, application_id) => {
      if (!title || !description || !application_id) return;
      
      setCheckingSimilar(true);
      try {
        const response = await ticketAPI.checkSimilar({ title, description, application_id });
        setSimilarTickets(response.data.similar_tickets || []);
      } catch (error) {
        console.error('Failed to check similar tickets:', error);
      } finally {
        setCheckingSimilar(false);
      }
    }, 500),
    []
  );

  useEffect(() => {
    if (formData.title && formData.description && formData.application_id) {
      checkSimilarTickets(formData.title, formData.description, formData.application_id);
    }
  }, [formData.title, formData.description, formData.application_id, checkSimilarTickets]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
    // Reset application when client changes
    if (field === 'client_id') {
      setFormData(prev => ({ ...prev, application_id: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.client_id) newErrors.client_id = 'Client is required';
    if (!formData.application_id) newErrors.application_id = 'Application is required';
    if (!formData.department) newErrors.department = 'Department is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        setErrors(prev => ({ ...prev, file: `${file.name} is too large. Max size is 10MB` }));
        continue;
      }
      
      setUploadingFile(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await fileAPI.upload(formData);
        setAttachments(prev => [...prev, response.data]);
      } catch (error) {
        console.error('Failed to upload file:', error);
        setErrors(prev => ({ ...prev, file: `Failed to upload ${file.name}` }));
      } finally {
        setUploadingFile(false);
      }
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = async (fileId) => {
    try {
      await fileAPI.delete(fileId);
      setAttachments(prev => prev.filter(f => f.id !== fileId));
    } catch (error) {
      console.error('Failed to delete file:', error);
    }
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return Image;
    if (contentType?.includes('pdf') || contentType?.includes('document')) return FileText;
    return File;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const response = await ticketAPI.createTicket(formData);
      const ticketId = response.data.id;
      
      // Update attachments with ticket ID
      for (const attachment of attachments) {
        await fileAPI.linkToTicket(attachment.id, ticketId);
      }
      
      navigate(`/tickets/${ticketId}`);
    } catch (error) {
      console.error('Failed to create ticket:', error);
      setErrors({ submit: error.response?.data?.detail || 'Failed to create ticket' });
    } finally {
      setLoading(false);
    }
  };

  const typeOptions = [
    { value: 'bug', label: 'Bug / Issue', icon: Bug, color: 'text-red-600 bg-red-100' },
    { value: 'requirement', label: 'New Requirement', icon: Lightbulb, color: 'text-blue-600 bg-blue-100' },
    { value: 'change_request', label: 'Change Request', icon: RefreshCw, color: 'text-purple-600 bg-purple-100' }
  ];

  return (
    <div className="max-w-3xl mx-auto" data-testid="ticket-form-page">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/tickets')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Tickets
        </button>
        <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">
          Create New Ticket
        </h1>
        <p className="text-muted-foreground mt-1">
          Report a bug, request a new feature, or submit a change request
        </p>
      </div>

      {/* Similar Tickets Warning */}
      {similarTickets.length > 0 && (
        <div className="similar-warning mb-6 animate-fade-in" data-testid="similar-warning">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-yellow-800">Similar Tickets Found</h3>
              <p className="text-sm text-yellow-700 mt-1">
                Please check if any of these existing tickets match your issue:
              </p>
              <ul className="mt-3 space-y-2">
                {similarTickets.map((ticket) => (
                  <li key={ticket.id} className="flex items-center justify-between bg-white p-2 rounded-sm border border-yellow-200">
                    <span className="text-sm font-medium">{ticket.title}</span>
                    <span className="text-xs text-yellow-600">{ticket.similarity}% match • {ticket.status}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <Card className="border rounded-sm p-6 space-y-6">
          {/* Ticket Type */}
          <div className="space-y-3">
            <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Ticket Type *
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {typeOptions.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => handleChange('ticket_type', type.value)}
                  className={`p-4 border rounded-sm text-left transition-all ${
                    formData.ticket_type === type.value
                      ? 'border-primary ring-2 ring-primary/20'
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid={`type-${type.value}`}
                >
                  <div className={`w-10 h-10 rounded-sm ${type.color} flex items-center justify-center mb-3`}>
                    <type.icon className="h-5 w-5" />
                  </div>
                  <p className="font-semibold text-sm">{type.label}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Title *
            </Label>
            <Input
              id="title"
              placeholder="Brief summary of the issue or request"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className={`rounded-sm ${errors.title ? 'border-destructive' : ''}`}
              data-testid="title-input"
            />
            {errors.title && <p className="text-sm text-destructive">{errors.title}</p>}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Description *
            </Label>
            <Textarea
              id="description"
              placeholder="Provide detailed information about the issue or request"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={5}
              className={`rounded-sm resize-none ${errors.description ? 'border-destructive' : ''}`}
              data-testid="description-input"
            />
            {errors.description && <p className="text-sm text-destructive">{errors.description}</p>}
          </div>

          {/* Client and Application */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {isInternalUser ? (
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Client *
                </Label>
                <Select value={formData.client_id} onValueChange={(v) => handleChange('client_id', v)}>
                  <SelectTrigger className={`rounded-sm ${errors.client_id ? 'border-destructive' : ''}`} data-testid="client-select">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.client_id && <p className="text-sm text-destructive">{errors.client_id}</p>}
              </div>
            ) : (
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Organization
                </Label>
                <div className="flex items-center h-10 px-3 border rounded-sm bg-muted/50 text-sm" data-testid="client-readonly">
                  {clients.find(c => c.id === formData.client_id)?.name || user?.organization || 'Loading...'}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Application / System *
              </Label>
              <Select 
                value={formData.application_id} 
                onValueChange={(v) => handleChange('application_id', v)}
                disabled={!formData.client_id}
              >
                <SelectTrigger className={`rounded-sm ${errors.application_id ? 'border-destructive' : ''}`} data-testid="application-select">
                  <SelectValue placeholder={formData.client_id ? "Select application" : "Select client first"} />
                </SelectTrigger>
                <SelectContent>
                  {applications.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.application_id && <p className="text-sm text-destructive">{errors.application_id}</p>}
            </div>
          </div>

          {/* Department */}
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Department *
            </Label>
            <Select value={formData.department} onValueChange={(v) => handleChange('department', v)}>
              <SelectTrigger className={`rounded-sm w-48 ${errors.department ? 'border-destructive' : ''}`} data-testid="department-select">
                <SelectValue placeholder="Select department" />
              </SelectTrigger>
              <SelectContent>
                {departments.map((d) => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.department && <p className="text-sm text-destructive">{errors.department}</p>}
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Priority
            </Label>
            <Select value={formData.priority} onValueChange={(v) => handleChange('priority', v)}>
              <SelectTrigger className="rounded-sm w-48" data-testid="priority-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* File Attachments */}
          <div className="space-y-3">
            <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Attachments
            </Label>
            
            {/* Uploaded Files */}
            {attachments.length > 0 && (
              <div className="space-y-2">
                {attachments.map((file) => {
                  const FileIcon = getFileIcon(file.content_type);
                  return (
                    <div key={file.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-sm border">
                      <div className="flex items-center gap-3">
                        <FileIcon className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">{file.original_filename}</p>
                          <p className="text-xs text-muted-foreground">
                            {(file.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(file.id)}
                        className="p-1 hover:bg-destructive/10 rounded-sm text-muted-foreground hover:text-destructive"
                        data-testid={`remove-file-${file.id}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Upload Button */}
            <div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip"
                data-testid="file-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingFile}
                className="rounded-sm gap-2"
                data-testid="attach-file-btn"
              >
                {uploadingFile ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Paperclip className="h-4 w-4" />
                )}
                {uploadingFile ? 'Uploading...' : 'Attach Files'}
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                Supported: Images, PDF, Word, Excel, Text files (Max 10MB each)
              </p>
            </div>
            {errors.file && <p className="text-sm text-destructive">{errors.file}</p>}
          </div>

          {/* Error */}
          {errors.submit && (
            <div className="p-4 bg-red-50 border border-destructive rounded-sm text-destructive text-sm">
              {errors.submit}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/tickets')}
              className="rounded-sm"
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="rounded-sm gap-2"
              data-testid="submit-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Create Ticket
            </Button>
          </div>
        </Card>
      </form>
    </div>
  );
};

export default TicketForm;
